import React, { useState } from 'react';
import { useAppState } from 'src/lib/hooks';
import {
  AddressCard,
  Button,
  Col,
  FormFeedback,
  FormGroup,
  Formik,
  FormInput,
  Icon,
  Label,
  Modal,
  Row,
  StreetViewImage,
  toast,
  useApp
} from 'straightline-ui';
import { ACCESSORIAL_CODES } from 'straightline-utils/constants';
import { formatDate } from 'straightline-utils/time';

export function PreviewLocation({ values }) {
  const { address } = values;
  return (
    <div className="border rounded">
      <div className="bg-light rounded">
        {address?.city ? (
          <StreetViewImage
            apiKey={process.env.REACT_APP_GOOGLE_MAPS_KEY}
            className="mw-100 rounded"
            address={address}
            size="495x495"
          />
        ) : (
          <div
            className="d-flex align-items-center justify-content-center"
            style={{ height: 495 }}
          >
            <p>Choose an address</p>
          </div>
        )}
      </div>
      <div className="p-4">
        {address?.address_1 && (
          <div className="d-flex">
            <div className="avatar avatar-sm mr-3">
              <div className="avatar-title font-size-lg rounded-circle bg-light text-dark">
                <Icon type="truck" />
              </div>
            </div>
            <div>
              {address?.name && (
                <div className="font-weight-bold">{address.name}</div>
              )}
              <div>{address.address_1}</div>
              <div>{`${address.city}, ${address.state} ${address.postal_code}`}</div>
            </div>
          </div>
        )}

        {(address?.contact || address?.phone || address?.email) && (
          <div className="d-flex mt-4">
            <div className="avatar avatar-sm mr-3">
              <div className="avatar-title font-size-lg rounded-circle bg-light text-dark">
                <Icon type="at-sign" />
              </div>
            </div>
            <div>
              <div className="mb-2">
                <div>{formatDate(new Date(), 'iiii, LLLL d, yyyy')}</div>
                {address.appointment && <div>{address.appointment}</div>}
              </div>
              <div>{address.contact}</div>
              {address.phone && (
                <div className="mt-1">
                  <a
                    href={`tel:${address.phone}`}
                    className="d-flex align-items-center"
                  >
                    <Icon type="phone" className="mr-3" />
                    {address.phone}
                  </a>
                </div>
              )}
              {address.email && (
                <div className="mt-1 text-break">
                  <a
                    href={`mailto:${address.email}`}
                    className="d-flex align-items-center"
                  >
                    <Icon type="mail" className="mr-3" />
                    {address.email}
                  </a>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export const LocationCard = ({ shipment, type }) => {
  const label = type.charAt(0).toUpperCase() + type.slice(1);

  return (
    <div className="position-relative">
      <AddressCard shipment={shipment} type={type} />
      <ChangeLocationModal shipment={shipment} type={type}>
        <Button
          type="button"
          size="sm"
          color="white"
          className="shadow"
          style={{
            position: 'absolute',
            left: '0.75rem',
            top: '0.75rem'
          }}
        >
          Edit {label}
        </Button>
      </ChangeLocationModal>
    </div>
  );
};

function ChangeLocationModal({ shipment, type, children, ...rest }) {
  const app = useApp();
  const [isOpen, setIsOpen] = useState(false);
  const {
    state: { user }
  } = useAppState();

  const handleSubmit = async (data, formik) => {
    const label = type.charAt(0).toUpperCase() + type.slice(1);
    const address = data.address;

    const oldDate = shipment[`${type}_date`];
    const oldAddress = shipment[`${type}_address`];
    const oldAppointment = oldAddress.appointment;

    let changed = {};

    if (data.notes) {
      changed.notes = data.notes;
    }

    if (data.date !== oldDate) {
      changed.date = `From ${oldDate} to ${data.date}`;
    }

    if (data.appointment !== oldAppointment) {
      changed.appointment = `From '${oldAppointment}' to '${data.appointment}'`;
    }

    if (address.address_1 !== oldAddress.address_1) {
      const oldFull = `${oldAddress.address_1}, ${oldAddress.city}, ${oldAddress.state}, ${oldAddress.postal_code}`;
      const newFull = `${address.address_1}, ${address.city}, ${address.state}, ${address.postal_code}`;
      changed.address = `From '${oldFull}' to '${newFull}'`;
    }

    if (
      (!shipment.accessorial_codes && data.accessorial_codes.length > 0) ||
      (shipment.accessorial_codes &&
        shipment.accessorial_codes.length !== data.accessorial_codes.length)
    ) {
      changed.accessorial_codes = `From [${shipment.accessorial_codes}] to [${data.accessorial_codes}]`;
    }

    app
      .service('api/contact')
      .create({
        emails: [process.env.REACT_APP_SUPPORT_EMAIL],
        service_id: shipment.id,
        service: 'api/shipments',
        subject: `${label} Details Change - Shipment #${shipment.id} | ${shipment.reference_number}`,
        text: `${user.full_name} (${user.email}) is requesting to change the ${label} details for shipment ${shipment.id}`,
        preTag: JSON.stringify(changed, null, 2)
      })
      .then((offer) => {
        formik.setSubmitting(false);
        toast(
          <div className="p-3">
            <h2 className="text-success">We're on it!</h2>
            <div className="lead mb-0">
              We are processing your {label} Details change and will get back to
              you ASAP.
            </div>
          </div>,
          { hideProgressBar: true }
        );
        toggle();
      })
      .catch((error) => {
        toast.errors(error);
        if (error.formik) {
          formik.setErrors(error.formik);
        }
        formik.setSubmitting(false);
        toggle();
      });
  };

  const toggle = () => {
    setIsOpen(!isOpen);
  };

  const label = type.charAt(0).toUpperCase() + type.slice(1);

  const dateVal = type === 'pickup' ? shipment.pickup_date : shipment.due_date;
  const addressVal = shipment[`${type}_address`];
  const apptVal = addressVal.appointment;
  const accessorialCodesVal = ACCESSORIAL_CODES[type.toUpperCase()]
    .filter(({ value }) => {
      return (shipment.accessorial_codes || []).indexOf(value) > -1;
    })
    .map((accessorial) => accessorial.value);

  return (
    <React.Fragment>
      {React.cloneElement(children, {
        onClick: toggle,
        ...rest
      })}
      <Modal isOpen={isOpen} toggle={toggle} unmountOnClose={true}>
        {() => {
          return (
            <React.Fragment>
              <div className="p-4 d-flex justify-content-between align-items-center border-bottom">
                <h3 className="m-0">Change {label} Details</h3>
              </div>
              <div className="p-4">
                <p className="text-secondary">
                  Changes to addresses and dates must be verified by
                  Straightline. Please make adjustments below and include any
                  pertinent notes. Straightline will make any changes and get
                  back to you ASAP!
                </p>
                <Formik
                  onSubmit={handleSubmit}
                  initialValues={{
                    date: dateVal,
                    address: addressVal,
                    appointment: apptVal,
                    accessorial_codes: accessorialCodesVal,
                    notes: ''
                  }}
                  render={({ isSubmitting, submitForm }) => {
                    return (
                      <React.Fragment>
                        <FormGroup>
                          <Label>{label} Address*</Label>
                          <FormInput
                            type="address"
                            name="address"
                            disabled={isSubmitting}
                            placeholder="Enter an address"
                            useAddressDetails
                            useAddress2
                            isClearable
                          />
                          <FormFeedback>
                            Address is a required field
                          </FormFeedback>
                        </FormGroup>
                        <FormGroup>
                          <Label>{label} Date*</Label>
                          <FormInput
                            type="date"
                            icon="calendar"
                            name="date"
                            disabled={isSubmitting}
                          />
                          <FormFeedback name="date" />
                        </FormGroup>
                        <Row>
                          <Col sm="8"></Col>
                          <Col sm="4"></Col>
                        </Row>
                        <FormGroup>
                          <Label>{label} Appointment*</Label>
                          <FormInput
                            name="appointment"
                            disabled={isSubmitting}
                            placeholder="9am - 5pm"
                          />
                          <FormFeedback name="appointment" />
                        </FormGroup>
                        <FormGroup>
                          <Label>{label} Accessorials</Label>
                          <FormInput
                            type="check-buttons"
                            name="accessorial_codes"
                            options={ACCESSORIAL_CODES[type.toUpperCase()]}
                            disabled={isSubmitting}
                          />
                          <FormFeedback name="accessorial_codes" />
                        </FormGroup>
                        <FormGroup>
                          <Label>Notes</Label>
                          <FormInput
                            name="notes"
                            type="textarea"
                            rows="4"
                            disabled={isSubmitting}
                            placeholder="Please include any notes"
                          />
                          <FormFeedback name="notes" />
                        </FormGroup>
                        <div className="d-flex justify-content-between">
                          <Button
                            type="button"
                            color="white"
                            onClick={toggle}
                            disabled={isSubmitting}
                          >
                            Cancel
                          </Button>
                          <Button
                            type="button"
                            color="primary"
                            className={`mr-3 ${
                              isSubmitting ? 'is-loading' : ''
                            }`}
                            onClick={submitForm}
                            disabled={isSubmitting}
                          >
                            Submit
                          </Button>
                        </div>
                      </React.Fragment>
                    );
                  }}
                />
              </div>
            </React.Fragment>
          );
        }}
      </Modal>
    </React.Fragment>
  );
}
